import { PersonnelTeamMembership } from "./MatterPersonnelTeamMembership";
import { Badge, Button, Checkbox } from "@/components/ui";
import type { UserAccountStatus } from "@/lib/utils/user-account-status";
import { MultiSelect } from "@/components/ui/FormFields/MultiselectFormField/MultiselectFormField";
import { Pencil, Plus, Trash2 } from "lucide-react";
import type { ReactNode } from "react";

export function MatterPersonnelPeople({
  people,
  roles,
  rolesFor,
  nameFor,
  statusFor,
  profileFor,
  teamNamesByUserId,
  canEdit,
  primaryUserId,
  onPrimaryContactChange,
  onChangeRoles,
  onRemove,
  onEdit,
}: {
  people: string[];
  roles: { id: string; label: string }[];
  rolesFor: (userId: string) => string[];
  nameFor: (userId: string) => string;
  statusFor: (userId: string) => UserAccountStatus | undefined;
  profileFor: (userId: string) => ReactNode;
  teamNamesByUserId: Map<string, Set<string>>;
  canEdit: boolean;
  primaryUserId: string | null;
  onPrimaryContactChange: (userId: string | null) => void;
  onChangeRoles: (userId: string, roleIds: string[]) => void;
  onRemove: (userId: string) => void;
  onEdit: () => void;
}) {
  return (
    <section className="border-border overflow-hidden rounded-lg border">
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <h3 className="font-medium">People</h3>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-sm">
            {people.length} assigned
          </span>
          {canEdit && (
            <Button
              variant={people.length === 0 ? "outline" : "ghost"}
              size={people.length === 0 ? "sm" : "icon"}
              className={people.length === 0 ? "h-8 gap-1.5" : "size-8"}
              aria-label={people.length === 0 ? "Add people" : "Edit people"}
              onClick={onEdit}
            >
              {people.length === 0 ? (
                <>
                  <Plus className="size-4" aria-hidden="true" />
                  Add people
                </>
              ) : (
                <Pencil className="size-4" />
              )}
            </Button>
          )}
        </div>
      </div>
      <div className="bg-muted/50 text-muted-foreground border-border grid grid-cols-[minmax(0,1fr)_minmax(11rem,15rem)_8rem_2.25rem] gap-4 border-b px-5 py-2 text-xs font-medium tracking-wide uppercase max-sm:hidden">
        <span>User</span>
        <span>Matter roles</span>
        <span className="text-center">Primary contact</span>
        <span />
      </div>
      {people.length === 0 && (
        <p className="text-muted-foreground px-5 py-5 text-sm">
          No people assigned.
        </p>
      )}
      {people.map((userId) => (
        <div
          key={userId}
          className="border-border grid min-h-16 grid-cols-[minmax(0,1fr)_minmax(11rem,15rem)_8rem_2.25rem] items-center gap-4 border-b px-5 py-3 last:border-b-0 max-sm:grid-cols-[minmax(0,1fr)_2.25rem]"
        >
          <div className="flex min-w-0 items-center gap-3">
            {profileFor(userId)}
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2 text-sm">
                <span className="truncate">{nameFor(userId)}</span>
                {statusFor(userId) && (
                  <Badge variant="muted" className="shrink-0">
                    {statusFor(userId)}
                  </Badge>
                )}
              </div>
              <PersonnelTeamMembership names={teamNamesByUserId.get(userId)} />
            </div>
          </div>
          <MultiSelect
            aria-label={`Matter roles for ${nameFor(userId)}`}
            options={roles.map((role) => ({
              value: role.id,
              label: role.label,
            }))}
            selectedValues={rolesFor(userId)}
            setSelectedValues={() => {}}
            onValueChange={(values) => {
              onChangeRoles(userId, values);
            }}
            disabled={!canEdit}
            ignoreAllSelected
            showToggleAll={false}
            showChips={false}
            showClearButton={false}
            closeOnSelect
            modalPopover
            className="w-full text-left max-sm:col-span-2 max-sm:row-start-2"
            placeholder="Select roles"
          />
          <label className="flex items-center justify-center gap-2 max-sm:col-span-2 max-sm:justify-start">
            <span className="text-muted-foreground text-sm sm:hidden">
              Primary contact
            </span>
            <Checkbox
              checked={primaryUserId === userId}
              disabled={!canEdit}
              aria-label={`Make ${nameFor(userId)} the primary contact`}
              onCheckedChange={(checked) => {
                onPrimaryContactChange(checked ? userId : null);
              }}
            />
          </label>
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="justify-self-end max-sm:col-start-2 max-sm:row-start-1"
              aria-label={`Remove ${nameFor(userId)}`}
              onClick={() => {
                onRemove(userId);
              }}
            >
              <Trash2 />
            </Button>
          )}
        </div>
      ))}
    </section>
  );
}
